import { useEffect, useState } from "react";
import api from "@/lib/api";
import { dateIT } from "@/lib/format";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function CalendarPage() {
  const [bookings, setBookings] = useState([]);
  const [props, setProps] = useState([]);
  const [selected, setSelected] = useState("all");
  const [cur, setCur] = useState(new Date());

  useEffect(() => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
  }, []);

  const filtered = selected === "all" ? bookings : bookings.filter((b) => b.property_id === selected);
  const bookedDays = {};
  filtered.forEach((b) => {
    if (!b.checkin || !b.checkout) return;
    let d = new Date(b.checkin); const end = new Date(b.checkout);
    while (d < end) { bookedDays[d.toDateString()] = b; d = new Date(d.getTime() + 86400000); }
  });

  const year = cur.getFullYear(), month = cur.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const upcoming = [...filtered].filter((b) => b.checkin).sort((a, b) => new Date(a.checkin) - new Date(b.checkin)).slice(0, 20);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Calendario</h1><p className="text-muted-foreground text-sm mt-1">Disponibilità e prenotazioni</p></div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm" data-testid="calendar-property-filter">
          <option value="all">Tutti gli immobili</option>
          {props.map((p) => <option key={p.id} value={p.id}>{p.data?.name || "Immobile"}</option>)}
        </select>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-card rounded-xl border border-border/80 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => setCur(new Date(year, month - 1, 1))} className="h-9 w-9 rounded-lg border border-border grid place-items-center hover:bg-muted"><ChevronLeft className="h-4 w-4" /></button>
            <h2 className="font-display text-lg font-semibold">{MONTHS[month]} {year}</h2>
            <button onClick={() => setCur(new Date(year, month + 1, 1))} className="h-9 w-9 rounded-lg border border-border grid place-items-center hover:bg-muted"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-2">{DOW.map((d) => <div key={d} className="text-center text-[11px] font-semibold uppercase text-muted-foreground">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((date, i) => {
              if (!date) return <div key={i} />;
              const bk = bookedDays[date.toDateString()];
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div key={i} data-testid="calendar-day-cell" title={bk ? bk.guest_name : ""}
                  className={`aspect-square rounded-lg border text-sm flex flex-col items-center justify-center transition-colors ${bk ? "bg-rose-500/15 border-rose-500/30 text-rose-800" : "bg-emerald-500/5 border-emerald-500/20 text-foreground hover:bg-emerald-500/15"} ${isToday ? "ring-2 ring-primary" : ""}`}>
                  <span className="font-medium">{date.getDate()}</span>
                  {bk && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-0.5" />}
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/20 border border-emerald-500/30" /> Disponibile</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-500/20 border border-rose-500/30" /> Occupato</span>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border/80 p-5">
          <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Agenda</h2>
          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {upcoming.map((b) => (
              <div key={b.id} className="p-3 rounded-lg bg-muted/40 border border-border">
                <div className="font-medium text-sm">{b.guest_name}</div>
                <div className="text-xs text-muted-foreground">{b.property_name}</div>
                <div className="text-xs mt-1">{dateIT(b.checkin)} → {dateIT(b.checkout)}</div>
              </div>
            ))}
            {upcoming.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessuna prenotazione.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
